import { RECORD_STATUS_LABELS } from '@dyeatlas/shared'
import { useMutation } from '@tanstack/react-query'
import { BookMarked, FlaskConical } from 'lucide-react'
import { useEffect } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ExperimentWizard } from './wizard/ExperimentWizard'
import { ReferenceForm } from './wizard/ReferenceForm'
import { useRecordDraft } from './useRecordDraft'
import { Badge } from '@/components/ui/Badge'
import { Card, EmptyState, LoadingPanel, Notice } from '@/components/ui/misc'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { api } from '@/lib/api'
import { cn } from '@/lib/utils'

/**
 * One route for both record kinds. `/database/new` creates the draft first, because
 * autosave and photo uploads both need a record id to exist.
 */
export function RecordEditor() {
  const { recordId } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const create = useMutation({
    mutationFn: (kind: 'experiment' | 'reference') =>
      api.post<{ record: { id: string } }>('/records', { kind }),
    onSuccess: (result) => navigate(`/database/${result.record.id}/edit`, { replace: true }),
  })

  const { isCurator, isLoading: userLoading } = useCurrentUser()
  // Only curators may add references, so everyone else skips the chooser.
  const kindParam = userLoading ? null : isCurator ? searchParams.get('kind') : 'experiment'
  const isNew = !recordId

  useEffect(() => {
    if (isNew && kindParam && !create.isPending && !create.isSuccess) {
      create.mutate(kindParam === 'reference' ? 'reference' : 'experiment')
    }
    // Creating exactly once per mount is the point; `create` identity changes each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, kindParam])

  const draft = useRecordDraft(recordId)

  if (isNew && userLoading) return <LoadingPanel label="Opening the record" />
  if (isNew && !kindParam) return <KindChooser onChoose={(kind) => create.mutate(kind)} />
  if (isNew || draft.isLoading) return <LoadingPanel label="Opening the record" />

  if (draft.error) {
    return (
      <EmptyState
        title="That record is not available"
        description={draft.error.message}
      />
    )
  }

  const record = draft.record
  if (!record) return <LoadingPanel label="Opening the record" />

  const isReference = record.kind === 'reference'

  return (
    <>
      <header className="mb-5 flex items-end justify-between gap-4">
        <div>
          <p className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-wide text-ink-faint">
            {isReference ? <BookMarked className="size-3.5" /> : <FlaskConical className="size-3.5" />}
            {isReference ? 'Reference' : 'Experiment'} · {record.code}
          </p>
          <h1 className="mt-0.5 font-display text-[28px] font-semibold leading-tight text-ink">
            {(draft.values.title as string) || 'Untitled record'}
          </h1>
        </div>
        <Badge tone={record.status === 'draft' ? 'neutral' : 'info'}>
          {RECORD_STATUS_LABELS[record.status]}
        </Badge>
      </header>

      {record.sourceProjectId && (
        <div className="mb-5">
          <Notice tone="brand" title="Public Database copy">
            This is the published copy of an experiment from the project{' '}
            <strong>{record.projectName}</strong>. Your changes save straight to the Public Database.
            The project team keeps editing its own original separately.
          </Notice>
        </div>
      )}

      {record.inProjectReview && (
        <div className="mb-5">
          <Notice tone="caution" title="Locked while the project is reviewed">
            This record belongs to a project awaiting approval. It can be edited again once the
            review is done or the project owner withdraws it.
          </Notice>
        </div>
      )}

      {record.status === 'rejected' && record.rejectionReason && (
        <div className="mb-5">
          <Notice tone="caution" title="Returned by reviewers">
            {record.rejectionReason} Make the changes and finish the record again to resubmit.
          </Notice>
        </div>
      )}

      {isReference ? <ReferenceForm draft={draft} /> : <ExperimentWizard draft={draft} />}
    </>
  )
}

function KindChooser({ onChoose }: { onChoose: (kind: 'experiment' | 'reference') => void }) {
  const choices = [
    {
      kind: 'experiment' as const,
      icon: <FlaskConical className="size-6" />,
      title: 'Experiment',
      description:
        'A dyeing run you carried out: materials, mordanting, dye bath, aftertreatment and the measured colour. Seven steps, saved as you go.',
    },
    {
      kind: 'reference' as const,
      icon: <BookMarked className="size-6" />,
      title: 'Reference',
      description:
        'A recipe transcribed from a historical manuscript, with its source, materials and the colour as recorded. A single page.',
    },
  ]

  return (
    <div className="mx-auto max-w-2xl">
      <header className="mb-6 text-center">
        <h1 className="font-display text-[28px] font-semibold text-ink">New record</h1>
        <p className="mt-1 text-sm text-ink-muted">What are you adding to the database?</p>
      </header>

      <div className="grid grid-cols-2 gap-4">
        {choices.map((choice) => (
          <Card
            key={choice.kind}
            className={cn(
              'cursor-pointer p-5 transition-colors hover:border-brand-line hover:bg-brand-soft',
            )}
          >
            <button type="button" onClick={() => onChoose(choice.kind)} className="text-left">
              <span className="flex size-11 items-center justify-center rounded-full bg-brand-soft text-brand">
                {choice.icon}
              </span>
              <h2 className="mt-3 font-display text-lg font-semibold text-ink">{choice.title}</h2>
              <p className="mt-1 text-[13px] leading-relaxed text-ink-muted">
                {choice.description}
              </p>
            </button>
          </Card>
        ))}
      </div>
    </div>
  )
}
